import type { BasicBooking_loadingPreference } from "../../../../common/graphql/fragments/gql/BasicBooking";
import { Information } from "../../../../common/texts";
import {
  type ApprovalConfig,
  getApprovalAlertType,
  getApprovalBackground,
  getApprovalHeader,
  getApprovalInfo,
} from "../../../../common/utils/approvalInfo";
import type { BookingFields } from "../../../../common/utils/bookingFields";
import {
  type GoodsApproval,
  GoodsApprovalStatus,
  GoodsApprovalType,
} from "../../../../common/utils/GoodsApproval";
import { resolveHazardousGoodsApproved } from "../../../../common/utils/hazardousGoodsUtil";
import { resolveLoadingPreferenceApproved } from "../../../../common/utils/loadingPreferenceUtil";
import { shouldShowHazardousGoodsApproval } from "../../../../common/utils/shouldShowHazardousGoodsApproval";
import type { BookingStatus, ColumnId } from "../../../../gql/graphql";

export interface BookingDetailsLocationState {
  addEmptyGoodsItem?: boolean;
  scrollGoodsIntoView?: boolean;
}

function intersection<T>(setA: Set<T>, setB: Set<T>): Set<T> {
  const _intersection = new Set<T>();
  for (const elem of setB) {
    if (setA.has(elem)) {
      _intersection.add(elem);
    }
  }
  return _intersection;
}

export const getFieldsToEdit = (
  shownFields: BookingFields,
  editableFields: ColumnId[],
): BookingFields => {
  if (editableFields.length === 0) {
    return shownFields;
  } else {
    return intersection(shownFields, new Set(editableFields));
  }
};

export const getLoadingPreferenceApprovalInfo = (
  loadingPreference: BasicBooking_loadingPreference | null,
  loadingPreferenceStatusCode: string,
): ApprovalConfig | null => {
  const loadingPreferenceApproval: GoodsApproval = {
    status: resolveLoadingPreferenceApproved(
      loadingPreference,
      loadingPreferenceStatusCode,
    ),
    type: GoodsApprovalType.LoadingPreference,
  };

  return getApprovalInfo(loadingPreferenceApproval, undefined, true);
};

export const getHazardousGoodsBannerApprovalInfo = (
  hazardousGoods: boolean,
  hazardousGoodsApproved: boolean,
  bookingStatus: BookingStatus | null,
  hazardousGoodsWeight: number,
): ApprovalConfig | null => {
  if (
    !shouldShowHazardousGoodsApproval(bookingStatus, hazardousGoodsApproved)
  ) {
    return null;
  }

  const hazardousGoodsStatus = resolveHazardousGoodsApproved(
    false,
    hazardousGoods,
    hazardousGoodsApproved,
  );

  return {
    type: getApprovalAlertType(hazardousGoodsStatus),
    backgroundColor: getApprovalBackground(hazardousGoodsStatus),
    headerText: getApprovalHeader(hazardousGoodsStatus),
    descriptionText: getDetailsHazardousGoodsText(
      hazardousGoodsStatus,
      hazardousGoodsWeight,
    ),
  };
};

function getDetailsHazardousGoodsText(
  status: GoodsApprovalStatus,
  weight: number,
) {
  switch (status) {
    case GoodsApprovalStatus.Approved: {
      let text = Information.HazardousGoodsApprovedShort as string;

      if (weight > 0) {
        text += ` Weight: ${weight} kg.`;
      }

      return text;
    }
    case GoodsApprovalStatus.Requested:
      return Information.HazardousGoodsRequestedShort;
    case GoodsApprovalStatus.NotRequested:
    case GoodsApprovalStatus.Rejected:
      return "";
  }
}
